import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DollarSign,
  ShoppingCart,
  TrendingUp,
  CreditCard,
  Users,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { startOfDay, endOfDay } from "date-fns";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, DateRangePicker, ExportButton } from "@/components/admin/shared";
import type { DateRange } from "@/components/admin/shared";
import { RevenueChart } from "@/components/admin/dashboard/RevenueChart";
import { OrdersByStatus } from "@/components/admin/dashboard/OrdersByStatus";
import { TopProducts } from "@/components/admin/dashboard/TopProducts";
import { RegionRevenue } from "@/components/admin/dashboard/RegionRevenue";
import { RecentOrdersWidget } from "@/components/admin/dashboard/RecentOrdersWidget";
import { LowStockWidget } from "@/components/admin/dashboard/LowStockWidget";
import { LowStockAlertWidget } from "@/components/admin/dashboard/LowStockAlertWidget";
import { PendingApprovalsWidget } from "@/components/admin/dashboard/PendingApprovalsWidget";
import { useDashboardKPIs, useDashboardCharts } from "@/hooks/admin/useDashboard";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { Button } from "@cieda/djayma-ui";
import { Skeleton } from "@cieda/djayma-ui";

// ---------------------------------------------------------------------------
// Export columns for KPI data
// ---------------------------------------------------------------------------

const EXPORT_COLUMNS = [
  { key: "kpi", label: "Indicateur" },
  { key: "value", label: "Valeur" },
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Dashboard() {
  const { t } = useTranslation(["admin", "dashboard"]);
  useAdminPageTitle("Tableau de bord");

  // Date range state — defaults to today
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    from: startOfDay(new Date()),
    to: endOfDay(new Date()),
  }));

  // Data fetching — both hooks receive the same range so KPIs and charts stay in sync
  const {
    data: kpis,
    isLoading: kpisLoading,
    isError: kpisError,
    refetch: refetchKPIs,
  } = useDashboardKPIs(dateRange);
  const {
    data: charts,
    isLoading: chartsLoading,
    isError: chartsError,
    refetch: refetchCharts,
  } = useDashboardCharts(dateRange);

  // Export data from KPIs — use ?? 0 for optional fields so the CSV never
  // contains "undefined FCFA" when the admin's department hides a KPI.
  const exportData = useMemo(() => {
    if (!kpis) return [];
    return [
      { kpi: t("dashboard.kpi.revenue_today"), value: `${kpis.revenue_fcfa ?? 0} FCFA` },
      { kpi: t("dashboard.kpi.orders_today"), value: String(kpis.orders_count ?? 0) },
      { kpi: t("dashboard.kpi.aov"), value: `${kpis.avg_basket_fcfa ?? 0} FCFA` },
      { kpi: t("dashboard.kpi.active_customers", "Clients actifs"), value: String(kpis.active_customers ?? 0) },
      { kpi: t("dashboard.kpi.pending_payments"), value: String(kpis.pending_payments ?? 0) },
      { kpi: t("dashboard.kpi.low_stock", "Stock faible"), value: String(kpis.low_stock_count ?? 0) },
    ];
  }, [kpis, t]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-stone-900">
              {t("dashboard.title")}
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              {t("dashboard.welcome")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <ExportButton
              data={[...exportData]}
              columns={[...EXPORT_COLUMNS]}
              filename="djayma-dashboard-kpis"
            />
          </div>
        </div>

        {/* Error state with retry */}
        {(kpisError || chartsError) && (
          <div
            className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center sm:justify-between"
            role="alert"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-red-500" aria-hidden="true" />
              <p className="text-sm text-red-700">
                {t("dashboard.error", "Impossible de charger les données du tableau de bord.")}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5 border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => {
                if (kpisError) void refetchKPIs();
                if (chartsError) void refetchCharts();
              }}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Réessayer
            </Button>
          </div>
        )}

        {/* Row 1: KPI cards — 2x2 on mobile, 5-col on desktop */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {kpisLoading ? (
            // Skeleton placeholders while KPIs are loading
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-stone-200 bg-white p-5 space-y-3"
                aria-hidden="true"
              >
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-3 w-16" />
              </div>
            ))
          ) : (
            <>
              <KPICard
                label={t("dashboard.kpi.revenue_today")}
                value={kpis?.revenue_fcfa ?? 0}
                format="currency"
                icon={<DollarSign className="h-5 w-5" />}
                trend={kpis?.revenue_trend}
              />
              <KPICard
                label={t("dashboard.kpi.orders_today")}
                value={kpis?.orders_count ?? 0}
                format="number"
                icon={<ShoppingCart className="h-5 w-5" />}
                trend={kpis?.orders_trend}
              />
              <KPICard
                label={t("dashboard.kpi.aov")}
                value={kpis?.avg_basket_fcfa ?? 0}
                format="currency"
                icon={<TrendingUp className="h-5 w-5" />}
                trend={kpis?.aov_trend}
              />
              <KPICard
                label={t("dashboard.kpi.active_customers", "Clients actifs")}
                value={kpis?.active_customers ?? 0}
                format="number"
                icon={<Users className="h-5 w-5" />}
              />
              <KPICard
                label={t("dashboard.kpi.pending_payments")}
                value={kpis?.pending_payments ?? 0}
                format="number"
                icon={<CreditCard className="h-5 w-5" />}
              />
            </>
          )}
        </div>

        {/* Row 2: Revenue chart — full width */}
        <RevenueChart
          data={charts?.revenue_series ?? []}
          isLoading={chartsLoading}
        />

        {/* Row 3: Orders by status + Top products — 50/50 split */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <OrdersByStatus
            data={charts?.orders_by_status ?? []}
            isLoading={chartsLoading}
          />
          <TopProducts
            data={charts?.top_products ?? []}
            isLoading={chartsLoading}
          />
        </div>

        {/* Row 4: Region revenue — full width */}
        <RegionRevenue
          data={charts?.region_revenue ?? []}
          isLoading={chartsLoading}
        />

        {/* Products section — compact event-sourced inventory widgets.
            Each widget self-hides when there's nothing to show. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LowStockAlertWidget />
          <PendingApprovalsWidget />
        </div>

        {/* Row 5: Recent orders + Low stock alerts — 60/40 split */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <RecentOrdersWidget
              orders={kpis?.recent_orders ?? []}
              isLoading={kpisLoading}
            />
          </div>
          <div className="lg:col-span-2">
            <LowStockWidget
              lowStockCount={kpis?.low_stock_count ?? 0}
              isLoading={kpisLoading}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
